import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import ReferenceForm from '../forms/ReferenceForm';
import useReferences from '../../hooks/useReferences';
import useProfile from '../../hooks/UseProfile';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import ReferenceList from './ReferenceList';
import { useQueryClient } from 'react-query';

const ReferenceSection = ({ cv, compact = false, triggerAdd, readonly = false }) => {
    const queryClient = useQueryClient();
    const { references, isLoading } = useReferences(cv?.id);
    const { refetch: refreshProfile } = useProfile();
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReference, setSelectedReference] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const prevTriggerRef = useRef(0);

    const handleAdd = () => {
        setSelectedReference(null);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (triggerAdd && triggerAdd !== prevTriggerRef.current) {
            prevTriggerRef.current = triggerAdd;
            handleAdd();
        }
    }, [triggerAdd]);

    const handleEdit = (reference) => {
        setSelectedReference(reference);
        setIsModalOpen(true);
    };

    const handleDelete = (reference) => {
        setSelectedReference(reference);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        queryClient.invalidateQueries(['references', cv?.id ?? null]);
        await refreshProfile();
        setIsDeleteModalOpen(false);
    };

    const content = (
        <>
            {error && (
                <Typography color="error" className="mb-4">
                    {error}
                </Typography>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <Typography>Loading references...</Typography>
                </Box>
            ) : (
                <ReferenceList
                    references={references}
                    onEdit={readonly ? undefined : handleEdit}
                    onDelete={readonly ? undefined : handleDelete}
                />
            )}

            <CustomModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                title={selectedReference ? 'Edit Reference' : 'Add New Reference'}
            >
                <ReferenceForm
                    id={selectedReference?.id}
                    onClose={() => setIsModalOpen(false)}
                    cvId={cv?.id}
                />
            </CustomModal>

            <DeleteDialog
                url={`/job/cv/reference/${selectedReference?.id}`}
                isOpen={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                notifyDone={handleConfirmDelete}
                message="Are you sure you want to delete this reference?"
            />
        </>
    );

    if (compact) {
        return content;
    }

    return (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    References
                </Typography>
                {!readonly && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                )}
            </Box>
            {content}
        </Paper>
    );
};

export default ReferenceSection; 